// ════════════════════════════════════════════════════════════
//  CrowdProps.jsx — A FEW SIGNS AND A FEW BEACH BALLS IN THE STANDS
// ════════════════════════════════════════════════════════════
//  caP'n, 2026-10-16: fans holding up signs with the fighters' names or just
//  random stuff (V'ael, the Pierre…), beach balls, "random stuff happening in the
//  stands, but not tooo much". Dials + words live in data/ringside.js (CROWD_PROPS,
//  SIGN_GAGS). This file never edits the crowd — it only READS its seat plan and
//  island heights, so the signs sit on the exact same fans and ride the same islands.
//
//  SIGNS — one InstancedMesh (one draw call). Every sign is a billboard quad that
//  copies its fan's bob and hop (same formula as StadiumCrowd's shader), sways a
//  little, and lives a loop: raise → wave → lower → come back as something else.
//  Every sign is a cell in ONE canvas atlas. 8 cells are the FIGHTERS' (A's and
//  B's names + their six beings), redrawn whenever the pairing changes; the rest
//  are the gags.
//  BALLS — a few beach balls lobbed across the heads of one island, then gone for
//  a good while. They ride islandBob too.
//
//  Nothing shows until the crowd's photo shoot is done (cue.crowdReady, set by
//  StadiumCrowd's Seats) — a sign floating over an empty stand would look broken.
// ════════════════════════════════════════════════════════════
import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { mulberry32 } from "../ieBeingModels.jsx";
import { CROWD_PROPS as P, SIGN_GAGS, CROWD_HEIGHT, WAVE } from "../data/ringside.js";
import { LINES } from "../data/roster.js";
import { say } from "../data/text.js";
import { ISLANDS, islandBob, seatPlan } from "./StadiumCrowd.jsx";

// ── the atlas: 8 fighter cells first, then the gags ──
const DYN = 8;                                   // [A name, B name, A's three, B's three]
const CELL_W = 256, CELL_H = 192, COLS = 4;
const CELLS = DYN + SIGN_GAGS.length;
const ROWS = Math.ceil(CELLS / COLS);
const BOARD_H = 128;                             // the board's share of a cell; the rest is the stick
const STYLES = [                                 // board / ink / edge — bright, readable, a little hand-made
  ["#f3e3b3", "#2a1a3a", "#a98a52"], ["#ffffff", "#7a1f4f", "#c9c9d6"], ["#ffd84d", "#2b1b00", "#b8921a"],
  ["#8be9c8", "#10323a", "#3a9d82"], ["#ff9bc4", "#3a0f26", "#c4558a"], ["#9fd8ff", "#10213f", "#4a8ac4"], ["#c8b6ff", "#24124a", "#7d63c9"],
];
const FONT = '"Comic Sans MS","Chalkboard SE","Marker Felt","Trebuchet MS",sans-serif';

function drawCell(ctx, i, text) {
  const col = i % COLS, row = Math.floor(i / COLS);
  drawBoard(ctx, col * CELL_W, row * CELL_H, text, STYLES[i % STYLES.length]);
}
// ONE LOOK FOR EVERY HELD SIGN: the crowd's boards and the Sky Box's (three/SkyBox.jsx) are drawn
// by this same function, so a sign in your hands looks like a sign in the stands.
// A long line (a chat message on a Sky Box sign, up to SIGN.max) wraps to three lines.
export const SIGN_CELL = { w: 256, h: 192, board: 128 };
export function drawBoard(ctx, x0, y0, text, colors) {
  const [bg, ink, edge] = colors;
  ctx.clearRect(x0, y0, CELL_W, CELL_H);
  ctx.fillStyle = "#6b4a2a"; ctx.fillRect(x0 + CELL_W / 2 - 7, y0 + BOARD_H - 4, 14, CELL_H - BOARD_H + 4);   // the stick
  ctx.fillStyle = bg; ctx.strokeStyle = edge; ctx.lineWidth = 6;
  ctx.beginPath(); ctx.roundRect(x0 + 6, y0 + 6, CELL_W - 12, BOARD_H - 8, 14); ctx.fill(); ctx.stroke();
  // lines: honour "\n", else split a long one at the space nearest the middle
  let lines = String(text).split("\n");
  if (lines.length === 1 && text.length > 22 && text.includes(" ")) lines = wrap3(text);
  else if (lines.length === 1 && text.length > 11 && text.includes(" ")) {
    const mid = text.length / 2; let best = -1;
    for (let k = 0; k < text.length; k++) if (text[k] === " " && (best < 0 || Math.abs(k - mid) < Math.abs(best - mid))) best = k;
    lines = [text.slice(0, best), text.slice(best + 1)];
  }
  ctx.save(); ctx.beginPath(); ctx.rect(x0 + 12, y0 + 10, CELL_W - 24, BOARD_H - 18); ctx.clip();
  ctx.fillStyle = ink; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  let size = lines.length === 1 ? 58 : lines.length === 2 ? 44 : 34;
  const fit = () => { ctx.font = `bold ${size}px ${FONT}`; return Math.max(...lines.map((l) => ctx.measureText(l).width)); };
  while (fit() > CELL_W - 36 && size > 14) size -= 2;
  const lh = size * 1.08, cy = y0 + 6 + (BOARD_H - 8) / 2 - ((lines.length - 1) * lh) / 2;
  lines.forEach((l, k) => ctx.fillText(l, x0 + CELL_W / 2, cy + k * lh));
  ctx.restore();
}
// three roughly even lines, broken at spaces (a word longer than a line just shrinks the font)
function wrap3(text) {
  const words = text.split(" "), target = text.length / 3, out = [""];
  words.forEach((w) => {
    const cur = out[out.length - 1];
    if (cur && cur.length + 1 + w.length > target + 4 && out.length < 3) out.push(w);
    else out[out.length - 1] = cur ? cur + " " + w : w;
  });
  return out;
}
const upper = (s) => String(s || "").toUpperCase();
// what the 8 fighter cells say for a given pairing (fall back to a gag, never a blank board)
function dynTexts(names, teams) {
  const nm = (id) => upper(LINES[id]?.name || id);
  const gag = (i) => (SIGN_GAGS.length ? SIGN_GAGS[i % SIGN_GAGS.length] : "");   // an emptied gag list is allowed: no gag, never a crash
  const t = [names && names.A ? say("signs.fighterA", { A: upper(names.A) }) : gag(0), names && names.B ? say("signs.fighterB", { B: upper(names.B) }) : gag(1)];
  for (let s = 0; s < 2; s++) for (let k = 0; k < 3; k++) { const id = teams && teams[s] && teams[s][k]; t.push(id ? say("signs.being", { name: nm(id) }) : gag(2 + s * 3 + k)); }
  return t;
}

const VERT = /* glsl */`
  attribute vec3 aSeat;     // x = the fan's phase, y = the fan's quad height, z = island
  attribute float aTex;     // which cell
  attribute float aUp;      // 0 lowered … 1 raised
  uniform float uTime, uCheer, uCorner, uW, uH, uCols, uRows, uWaveHead, uWaveAmp;
  uniform float uIsle[${ISLANDS.length}];
  varying vec2 vUv;
  void main() {
    if (aUp < 0.02) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); return; }
    vec3 c = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    c.y += uIsle[int(aSeat.z + 0.5)];
    float mine = uCorner == 0.0 ? 1.0 : (sign(c.x) == uCorner ? 1.0 : 0.45);   // same as the fans'
    float ph = aSeat.x;
    float hop = max(0.0, sin(uTime * (5.0 + fract(ph * 1.7) * 2.0) + ph));
    c.y += sin(uTime * 1.4 + ph) * 0.03 + hop * hop * 0.55 * uCheer * mine;   // the fan's own bob + hop, so the sign never leaves its hands
    float wd = mod(atan(c.x, c.z) - uWaveHead + 3.14159265, 6.2831853) - 3.14159265;   // the wave lifts the sign with its fan
    c.y += exp(-wd * wd * ${WAVE.width.toFixed(1)}) * uWaveAmp * ${WAVE.height.toFixed(2)};
    c.y += aSeat.y * 0.55;                                                    // held at chest height
    float th = sin(uTime * 1.3 + ph * 2.0) * 0.07 + hop * uCheer * mine * 0.12 * sin(uTime * 3.0 + ph);
    vec2 l = vec2(position.x * uW, position.y * uH * aUp);                    // rises out of the hands
    l = vec2(l.x * cos(th) - l.y * sin(th), l.x * sin(th) + l.y * cos(th));
    vec3 right = normalize(vec3(viewMatrix[0][0], 0.0, viewMatrix[2][0]));
    vec3 toCam = normalize(cameraPosition - c);
    vec3 p = c + right * l.x + vec3(0.0, l.y, 0.0) + toCam * 0.35;            // a touch in front of its fan
    float col = mod(aTex, uCols), row = floor(aTex / uCols);
    vUv = vec2((col + uv.x) / uCols, 1.0 - (row + 1.0 - uv.y) / uRows);
    gl_Position = projectionMatrix * viewMatrix * vec4(p, 1.0);
  }
`;
const FRAG = /* glsl */`
  uniform sampler2D uMap;
  varying vec2 vUv;
  void main() {
    vec4 t = texture2D(uMap, vUv);
    if (t.a < 0.5) discard;
    gl_FragColor = vec4(t.rgb, 1.0);
    #include <tonemapping_fragment>
    #include <colorspace_fragment>
  }
`;

const between = (R, [lo, hi]) => lo + R() * (hi - lo);

function Signs({ cue, director, teams }) {
  const S = P.signs;
  const mesh = useRef();
  // which fans hold signs: a few seats from the FRONT rows of every island (seeded: the same fans every visit)
  const picks = useMemo(() => {
    const R = mulberry32(777), seats = seatPlan(), out = [];
    ISLANDS.forEach((isle, ii) => {
      const d = isle.deck, rMax = d.r0 + S.frontRows * d.dr;
      const pool = seats.filter((s) => s.isle === ii && Math.hypot(s.x, s.z) <= rMax);
      for (let n = 0; n < S.perIsland && pool.length; n++) out.push(pool.splice(Math.floor(R() * pool.length), 1)[0]);
    });
    return out;
  }, [S.perIsland, S.frontRows]);

  const atlas = useMemo(() => {
    const cv = document.createElement("canvas"); cv.width = COLS * CELL_W; cv.height = ROWS * CELL_H;
    const ctx = cv.getContext("2d");
    dynTexts(null, null).forEach((t, i) => drawCell(ctx, i, t));
    SIGN_GAGS.forEach((t, i) => drawCell(ctx, DYN + i, t));
    const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; tex.anisotropy = 4;
    return { cv, ctx, tex };
  }, []);

  // one little life per sign
  const life = useRef(null);
  const attrs = useMemo(() => {
    const R = mulberry32(31337);
    const n = picks.length, seat = new Float32Array(n * 3), tex = new Float32Array(n), up = new Float32Array(n);
    const pickTex = () => (R() < S.nameShare || !SIGN_GAGS.length ? Math.floor(R() * R() * DYN) : DYN + Math.floor(R() * SIGN_GAGS.length));   // R*R leans toward the first cells: the two NAME signs
    life.current = picks.map((s, i) => {
      seat[i * 3] = s.ph; seat[i * 3 + 1] = s.size; seat[i * 3 + 2] = s.isle;
      tex[i] = pickTex();
      const raised = R() < 0.65;
      up[i] = 0;
      return { R, raised, t: raised ? R() * between(R, S.up) : R() * between(R, S.down), cur: 0, pickTex };
    });
    return { seat, tex, up };
  }, [picks, S.nameShare, S.up, S.down]);

  const geo = useMemo(() => {
    const g = new THREE.PlaneGeometry(1, 1);
    g.translate(0, 0.5, 0);        // x −½…½ across, y 0…1 up from the hands
    g.setAttribute("aSeat", new THREE.InstancedBufferAttribute(attrs.seat, 3));
    g.setAttribute("aTex", new THREE.InstancedBufferAttribute(attrs.tex, 1));
    g.setAttribute("aUp", new THREE.InstancedBufferAttribute(attrs.up, 1));
    return g;
  }, [attrs]);
  const mat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader: VERT, fragmentShader: FRAG,
    uniforms: { uMap: { value: atlas.tex }, uTime: { value: 0 }, uCheer: { value: 0 }, uCorner: { value: 0 },
      uW: { value: S.size }, uH: { value: S.size * CELL_H / CELL_W }, uCols: { value: COLS }, uRows: { value: ROWS }, uWaveHead: { value: 0 }, uWaveAmp: { value: 0 },
      uIsle: { value: new Float32Array(ISLANDS.length) } },
  }), [atlas, S.size]);
  useLayoutEffect(() => {
    const m = mesh.current; if (!m) return;
    const o = new THREE.Object3D();
    picks.forEach((s, i) => { o.position.set(s.x, s.y, s.z); o.updateMatrix(); m.setMatrixAt(i, o.matrix); });
    m.instanceMatrix.needsUpdate = true;
  }, [picks]);
  useEffect(() => () => { geo.dispose(); mat.dispose(); atlas.tex.dispose(); }, [geo, mat, atlas]);

  const seen = useRef({ key: "", poll: 0, heat: 0, corner: null });
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05), c = cue.current, t = clock.elapsedTime, k = seen.current;
    if (mesh.current) mesh.current.visible = !!c.crowdReady;
    if (!c.crowdReady) return;
    // the fighters' signs follow the pairing (checked twice a second)
    k.poll -= dt;
    if (k.poll <= 0) {
      k.poll = 0.5;
      const names = director.current && director.current.names, tm = (director.current && director.current.teams) || teams;   // the fight hands its teams over through the director
      const key = `${names ? names.A + "|" + names.B : ""}#${tm.map((x) => (x || []).join(",")).join("/")}`;
      if (key !== k.key) { k.key = key; dynTexts(names, tm).forEach((s, i) => drawCell(atlas.ctx, i, s)); atlas.tex.needsUpdate = true; }
    }
    // Willie calls a corner → a handful of lowered signs pop up with THAT fighter's name
    if (c.corner !== k.corner) {
      k.corner = c.corner;
      if (c.corner === "A" || c.corner === "B") {
        const idle = life.current.map((L, i) => (L.raised ? -1 : i)).filter((i) => i >= 0);
        for (let n = 0; n < 6 && idle.length; n++) {
          const i = idle.splice(Math.floor(Math.random() * idle.length), 1)[0], L = life.current[i];
          L.raised = true; L.t = between(L.R, P.signs.up); attrs.tex[i] = c.corner === "A" ? 0 : 1; geo.attributes.aTex.needsUpdate = true;
        }
      }
    }
    // each sign: raise → wave → lower → back as something new
    const { tex, up } = attrs;
    life.current.forEach((L, i) => {
      L.t -= dt;
      if (L.t <= 0) {
        if (L.raised) { L.raised = false; L.t = between(L.R, S.down); }
        else if (L.cur <= 0.02) { L.raised = true; L.t = between(L.R, S.up); tex[i] = L.pickTex(); geo.attributes.aTex.needsUpdate = true; }
        else L.t = 0.2;
      }
      L.cur += ((L.raised ? 1 : 0) - L.cur) * Math.min(1, dt * 3.2);
      if (!L.raised && L.cur < 0.02) L.cur = 0;
      up[i] = L.cur;
    });
    geo.attributes.aUp.needsUpdate = true;
    // the same cheer / called-corner the fans read
    k.heat += ((c.cheer || 0) - k.heat) * Math.min(1, dt * 3);
    const u = mat.uniforms; u.uTime.value = t; u.uCheer.value = k.heat; u.uCorner.value = c.corner === "A" ? -1 : c.corner === "B" ? 1 : 0;
    u.uWaveHead.value = c.waveHead || 0; u.uWaveAmp.value = c.waveAmp || 0;
    for (let i = 0; i < u.uIsle.value.length; i++) u.uIsle.value[i] = islandBob(i, t);
  });
  return <instancedMesh ref={mesh} args={[geo, mat, picks.length]} frustumCulled={false} visible={false} />;
}

// ── beach balls: lobbed across the heads of one island, then gone a good while ──
function ballTexture() {
  const cv = document.createElement("canvas"); cv.width = 256; cv.height = 128;
  const ctx = cv.getContext("2d"), cols = ["#ff5d7a", "#ffffff", "#ffd84d", "#ffffff", "#5ad0ff", "#ffffff"];
  cols.forEach((c, i) => { ctx.fillStyle = c; ctx.fillRect((i * 256) / cols.length, 0, 256 / cols.length + 1, 128); });
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 256, 14); ctx.fillRect(0, 114, 256, 14);   // the white caps at the poles
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace; return tex;
}
function Balls({ cue }) {
  const B = P.balls, refs = useRef([]);
  const map = useMemo(ballTexture, []);
  const geo = useMemo(() => new THREE.SphereGeometry(B.radius, 20, 14), [B.radius]);
  const mat = useMemo(() => new THREE.MeshBasicMaterial({ map, toneMapped: false }), [map]);
  useEffect(() => () => { map.dispose(); geo.dispose(); mat.dispose(); }, [map, geo, mat]);
  const st = useRef(Array.from({ length: B.count }, (_, i) => ({ R: mulberry32(9100 + i), fly: false, wait: 3 + i * 4.5, e: 0, dur: 10, isle: 0, from: 0, to: 1, row: 4 })));
  useFrame(({ clock }, dtRaw) => {
    const dt = Math.min(dtRaw || 0.016, 0.05), t = clock.elapsedTime, ready = !!cue.current.crowdReady;
    st.current.forEach((b, i) => {
      const m = refs.current[i]; if (!m) return;
      if (!ready) { m.visible = false; return; }
      if (!b.fly) {
        m.visible = false; b.wait -= dt;
        if (b.wait <= 0) {   // out it comes, onto a random island, in a random direction
          b.fly = true; b.e = 0; b.dur = between(b.R, B.flight); b.isle = Math.floor(b.R() * ISLANDS.length);
          const flip = b.R() < 0.5; b.from = flip ? 0.94 : 0.06; b.to = flip ? 0.06 : 0.94;
          b.row = ISLANDS[b.isle].deck.rows * (0.3 + b.R() * 0.4);
        }
        return;
      }
      b.e += dt;
      if (b.e >= b.dur) { b.fly = false; b.wait = between(b.R, B.rest); m.visible = false; return; }
      const isle = ISLANDS[b.isle], d = isle.deck, f = b.e / b.dur;
      const a = isle.phi0 + isle.len * (b.from + (b.to - b.from) * f), r = d.r0 + (b.row + 0.5) * d.dr;
      const bounce = Math.abs(Math.sin((Math.PI * b.e) / B.lob));
      const y = d.y0 + b.row * d.dy + islandBob(b.isle, t) + CROWD_HEIGHT.champion + 0.5 + bounce * 1.7;
      m.position.set(Math.sin(a) * r, y, Math.cos(a) * r);
      m.rotation.set(b.e * 1.7, b.e * 2.3, 0);
      m.scale.setScalar(Math.min(1, b.e / 0.6, (b.dur - b.e) / 0.6));   // grows in and shrinks out: never pops
      m.visible = true;
    });
  });
  return <>{st.current.map((_, i) => <mesh key={i} ref={(el) => (refs.current[i] = el)} geometry={geo} material={mat} visible={false} frustumCulled={false} />)}</>;
}

export default function CrowdProps({ cue, director, teams }) {
  return <><Signs cue={cue} director={director} teams={teams} /><Balls cue={cue} /></>;
}
